import {Document, Font, Image, Link, Page, Text, View} from '@react-pdf/renderer';
import "native-injects";
import brand from "../../../assets/icons/brand/brand.png";
import brandLight from "../../../assets/icons/brand/brand_light.png";
import addRounded from "../../../assets/images/executive_summary/add_rounded.png";
import {propertyDisplayBudgetYear} from "../../../contexts/properties/logic";
import {formatterDateTimeUS, formatterDollarUSNoDecimal, formatterPercentWithSign} from '../../../utils/formatters';
import {round} from '../../../utils/math';
import {getVarianceColor} from '../../../utils/variance';
import {IClientBrandingAssetBuilt} from '../constants';
import {ReportTableData} from '../logic/reportTablesLogic';
import {ExecutiveSummaryGraphData} from '../logic/seriesGraphDataLogic';
import {PropertyExecutiveSummaryData, RenovationsSummaryTableData, SERIES_DATA_OPTION} from "../logic/usePropertyExecutiveSummaryData";
import styles, {interFontDefinitions} from "./pdfStyles";

export interface IExecutiveSummaryPDFProps {
    data: PropertyExecutiveSummaryData,
    charts: Map<SERIES_DATA_OPTION, string>,
    viewOnly: boolean,
    rawChartData: Map<SERIES_DATA_OPTION, ExecutiveSummaryGraphData>,
}

export interface IMetricBlockValue {
    value: string;
    topLabel?: JSX.Element | string;
    bottomLabel?: JSX.Element | string;
}

export interface IMetricBlock {
    title: string;
    isSmallTitle?: boolean;
    values?: IMetricBlockValue[];
    text: JSX.Element | string;
}

export interface IExecutiveSummaryPDFExportProps {
    data: PropertyExecutiveSummaryData,
    customT12Text: string,
    charts: Map<SERIES_DATA_OPTION, string>,
    rawChartData: Map<SERIES_DATA_OPTION, ExecutiveSummaryGraphData>,
    financialOverviewData: string[][][],
    revenueSummaryData: string[][][],
    payrollTableData: string[][][],
    title_by_series_option: Record<string, string>,
    fullExecutiveSummaryStatementNOIWithoutExpense: string,
    executiveSummaryStatementNOI: string,
    executiveSummaryStatementTotalExpense: string,
    customNarrativeParagraphs: string[],
    rentsStatementSummary: string,
    execSummaryMetricData: Record<string, IMetricBlock>,
    clientBranding: IClientBrandingAssetBuilt,
    // you cant import into the worker without it crashing, so we have to pass it in
    assetLivingWorkspaceIds: string[],
}

export interface IExecutiveSummaryPDFExportWorkerProps extends Omit<IExecutiveSummaryPDFExportProps, 'execSummaryMetricData'> {
    execSummaryMetricData: string,
}

export function chunkArray(array: string[][], size: number) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
}

const ROWS_PER_PAGE_RENOVATIONS = 20;

export function execSummaryPDFExport(props: IExecutiveSummaryPDFExportProps) {
    const {
        data,
        customT12Text,
        rawChartData,
        charts,
        title_by_series_option,
        fullExecutiveSummaryStatementNOIWithoutExpense,
        executiveSummaryStatementTotalExpense,
        executiveSummaryStatementNOI,
        customNarrativeParagraphs,
        financialOverviewData,
        revenueSummaryData,
        payrollTableData,
        rentsStatementSummary,
        execSummaryMetricData,
        clientBranding,
        assetLivingWorkspaceIds,
    } = props;

    Font.register(interFontDefinitions);
    Font.registerHyphenationCallback((word) => {
        return [word];
    });

    const backgroundImageStyle = {
        ...styles.backgroundImage,
        objectPosition: clientBranding.pdfImagePosition ? clientBranding.pdfImagePosition : "center",
    };

    // the calculation here translates the visual logo size from the executive summary config pdf preview
    // to a corresponding size on the full pdf
    let coverLogoStyle = {
        ...styles.footerPartnerLogo,
        width: clientBranding.pdfLogoSize ? clientBranding.pdfLogoSize / 100 * 230 : styles.footerPartnerLogo.width,
    };

    // TODO:bowman revisit for more sustainable per-client overrides
    if (clientBranding.name == "ti") {
        coverLogoStyle = {
            ...coverLogoStyle,
            ...styles.tiLogo,
        };
    } else if (clientBranding.name == "haven") {
        coverLogoStyle = {
            ...coverLogoStyle,
            ...styles.havenLogo,
        };
    }

    function renderSectionAnchor(anchorText: string) {
        return (
            <View id={anchorText} render={({pageNumber}) => {
                if (!sectionStartPageMap.has(anchorText)) {
                    sectionStartPageMap.set(anchorText, pageNumber);
                }
                return null;
            }}/>
        );
    }

    const sectionStartPageMap: Map<string, number> = new Map();

    function buildTableOfContentsItems() {
        const toc: [string, string][] = [
            ["Analysis", "analysis"],
            ["Operational Metric Charts", "ops-metric-charts"],
            ["Financial Overview", "financial-overview"],
            ["Revenue Summary", "rev-summary"],
            ["Renovations", "renovations"],
            ["Payroll", "payroll"],
        ];

        if (data.reportTables.length > 0) {
            toc.splice(1, 0, ["Financial Tables", "financial-tables"]);
        }

        if (clientBranding.showSignOffPage) {
            toc.push(["Budget Sign-Off", "budgetsignoff"]);
        }

        return (
            toc.map(([title, anchor], i) =>
                <Link src={`#${anchor}`} style={styles.tocItem} key={i}>
                    <Text style={styles.tocTitle}>{title}</Text>
                    <Text style={styles.tocPageNumber} render={() => {
                        const sectionStartPage = sectionStartPageMap.get(anchor);
                        if (sectionStartPage === undefined) {
                            return "";
                        }
                        return String(sectionStartPage - 1);
                    }} />
                </Link>
            )
        );
    }

    const tableOfContents = (
        <Page size="LETTER" style={styles.page}>
            <View style={styles.pageTitleWrapper} fixed>
                <Text style={styles.pageTitle}>Table of Contents</Text>
                <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
            </View>

            {buildTableOfContentsItems()}
            {pageFooter()}
        </Page>
    );

    function pageFooter() {
        return (
            <View style={styles.pageFooter} fixed>
                <Image src={brand} style={styles.pageFooterLogo} />
                <Text style={styles.pageFooterTimestamp} render={({ pageNumber }) => {
                    const adjustedPageNumber = pageNumber - 1;
                    return adjustedPageNumber > 1 ? formatterDateTimeUS(new Date()) : " ";
                }} />
                <Text style={styles.pageFooterNumber} render={({ pageNumber }) => {
                    const adjustedPageNumber = pageNumber - 1;
                    return adjustedPageNumber > 1 ? adjustedPageNumber : " ";
                }} />
            </View>
        );
    }

    function renderSignOffPageTable() {
        let signOffTableToUse = data.signOffPageTable;
        if (data.isUsingCustomT12) {
            signOffTableToUse = data.customT12SignOffPageTable;
        }
        const signOffPageTableRows = signOffTableToUse?.rows ?? [];
        if (!signOffTableToUse || signOffPageTableRows.length === 0) {
            return <></>;
        }

        return (
            <View wrap={false} style={{ ...styles.cardWrapper }}>
                <View style={styles.cardHeader}>
                    <Text style={styles.cardHeaderTitle}>Summary</Text>
                    <Text style={styles.cardHeaderSubtitle}>{propertyDisplayBudgetYear(data.property)}</Text>
                </View>

                <View style={{ ...styles.cardBody, ...styles.cardBodyFlush }}>
                    <View style={styles.tableRow}>
                        <View style={{ ...styles.tableCellWrapper, ...styles.reportTableNameCol, ...styles.boldText }}>
                            <Text style={styles.tableCell}></Text>
                        </View>

                        <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...styles.boldText, ...styles.finalCol }}>
                            <Text style={styles.tableCell}>TOTAL</Text>
                        </View>
                    </View>

                    {signOffPageTableRows.map((row, rowIndex) => {
                        let tableRowStyle = {...styles.tableRow};
                        if (rowIndex === signOffPageTableRows.length - 1) {
                            tableRowStyle = {...tableRowStyle, ...styles.finalRow};
                        }
                        return (
                            <View key={row.accountId} style={tableRowStyle}>
                                <View style={{ ...styles.tableCellWrapper, ...styles.reportTableNameCol }}>
                                    <Text style={styles.tableCell}>{row.name}</Text>
                                </View>

                                <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...styles.finalCol }}>
                                    <Text style={styles.tableCell}>{row.budgetTotal === null ? "-" : formatterDollarUSNoDecimal.format(row.budgetTotal)}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>
            </View>
        );
    }

    function renderFinancialsTable(rows: string[][]) {
        const columnCount = 18;
        const rowCount = rows.length;

        return (
            <View wrap={false} style={{ ...styles.cardWrapper, ...styles.cardWrapperFull }}>
                <View style={{ ...styles.cardBody, ...styles.cardBodyFlush }}>
                    {
                        rows.map((row, rowIndex) =>
                            <View style={styles.tableRow} key={`row-${rowIndex}`}>
                                {
                                    row.map((r, colIndex) => {
                                        const isComponentRow = !!row[0]?.includes("__COMPONENT");
                                        const isCategoryRow = !!row[0]?.includes("__CATEGORY");
                                        const isFirstCol = colIndex === 0;
                                        let wrapperStyles = {};
                                        let cellText = r;

                                        if (colIndex == 0) {
                                            wrapperStyles = styles.financialsTableGLCol;
                                        } else if (colIndex == 1) {
                                            wrapperStyles = styles.financialsTableNameCol;
                                        } else {
                                            wrapperStyles = styles.financialsTableDataCol;
                                        }

                                        if (colIndex == columnCount - 1) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.finalCol };
                                        }

                                        if (rowIndex == 0) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.financialsTableHeaderRow };
                                        }

                                        if (rowIndex == rowCount - 1) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.finalRow };
                                        }

                                        // component and category rows get different styling, and I'm using a string token to detect
                                        // which one to use for the row, removing the token portion of the string, and applying the correct styling
                                        if (isComponentRow || isCategoryRow) {
                                            cellText = isFirstCol ? (cellText.split("__")[0] ?? "") : cellText;
                                            const rowStyle = isComponentRow ? styles.financialsTableSummaryRowDarker : styles.financialsTableSummaryRow;
                                            wrapperStyles = { ...wrapperStyles, ...rowStyle };
                                        }

                                        // Dec col, to show thick border before totals
                                        if (colIndex == 13) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.financialsTableThickBorderRight };
                                        }

                                        return (
                                            <View style={{ ...styles.tableCellWrapper, ...wrapperStyles }} key={`row-${rowIndex}-cell-${colIndex}`}>
                                                <Text style={styles.financialsTableCell}>{cellText}</Text>
                                            </View>
                                        )
                                    })
                                }
                            </View>
                        )
                    }
                </View>
            </View>
        );
    }

    function renderRevenueTable(rows: string[][], tableIndex: number) {
        const firstRow = rows[0];

        if (!firstRow) {
            return <></>;
        }

        const columnCount = firstRow.length;
        const rowCount = rows.length;

        return (
            <View wrap={false} style={{ ...styles.cardWrapper, ...styles.cardWrapperFull }} key={tableIndex}>
                <View style={{ ...styles.cardBody, ...styles.cardBodyFlush }}>
                    {
                        rows.map((row, rowIndex) =>
                            <View style={styles.tableRow} key={`row-${rowIndex}`}>
                                {
                                    row.map((text, colIndex) => {
                                        const isMetricHeader = !!row[0]?.includes("__METRIC_HEADER");
                                        const isFirstCol = colIndex === 0;
                                        let wrapperStyles = {};
                                        let cellText = text;

                                        if (colIndex == 0) {
                                            wrapperStyles = styles.financialsTableNameCol;
                                        } else {
                                            wrapperStyles = styles.revenueTableDataCol;
                                        }

                                        if (colIndex == columnCount - 1) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.finalCol };
                                        }

                                        if (rowIndex == 0) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.financialsTableHeaderRow };
                                        }

                                        if (rowIndex == rowCount - 1) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.finalRow };
                                        }

                                        if (row.length == 1) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.financialsTableSummaryRowDarker };
                                        }

                                        if (isMetricHeader) {
                                            cellText = isFirstCol ? (cellText.split("__")[0] ?? "") : cellText;
                                            wrapperStyles = { ...wrapperStyles, ...styles.financialsTableSummaryRow };
                                        }

                                        return (
                                            <View style={{ ...styles.tableCellWrapper, ...wrapperStyles }} key={`row-${rowIndex}-cell-${colIndex}`}>
                                                <Text style={styles.revenueTableCell}>{cellText.length == 0 ? " " : cellText}</Text>
                                            </View>
                                        )
                                    })
                                }
                            </View>
                        )
                    }
                </View>
            </View>
        );
    }

    function buildMetricBlock(metricData: IMetricBlock | undefined, metricType: string) {
        if (!metricData) {
            return <></>;
        }

        return (
            <View style={styles.metricBlock} key={metricType}>
                <Text style={{ ...styles.title, ...styles.metricBlockTitle }}>{metricData.title}</Text>
                <View style={styles.metricBlockValues}>
                    {metricData.values && metricData.values.map((v, i) => {
                        return (
                            <View key={i} style={styles.metricBlockValueWrapper}>
                                <Text style={styles.metricBlockTopLabel}>{v.topLabel}</Text>
                                <Text style={styles.metricBlockValue}>{v.value}</Text>
                                {
                                    v.bottomLabel &&
                                    <Text style={styles.metricBlockBottomLabel}>{v.bottomLabel}</Text>
                                }
                            </View>
                        );
                    })}
                </View>
                <Text style={styles.metricBlockText}>{metricData.text}</Text>
            </View>
        );
    }

    function buildChartView(type: SERIES_DATA_OPTION, chartData: string) {
        const chartName = title_by_series_option[type];
        const chartMetricData = rawChartData.get(type);

        if (!chartName || !chartMetricData) {
            return <></>;
        }

        const { comparisonItemOne, comparisonItemTwo, varianceAmount, variancePercent } = chartMetricData.summary;

        return (
            <View wrap={false} style={styles.cardWrapper} key={chartName}>
                <View style={styles.cardHeader}>
                    <Text style={styles.cardHeaderTitle}>{chartName}</Text>
                    <Text style={styles.cardHeaderSubtitle}>{!data.isUsingCustomT12 && `${data.property.reforecastYear} RFCST vs `}{propertyDisplayBudgetYear(data.property) % 100} BDGT</Text>
                </View>
                <View style={styles.cardBody}>
                    {!data.isUsingCustomT12 &&
                        <View style={styles.chartBodyHeader}>
                            <View style={styles.chartBodyMetric}>
                                <Text style={styles.chartBodyMetricLabel}>{comparisonItemOne.label}</Text>
                                <Text style={styles.chartBodyMetricValue}>{comparisonItemOne.value}</Text>
                            </View>
                            <View style={styles.chartBodyMetric}>
                                <Text style={styles.chartBodyMetricLabel}>{comparisonItemTwo.label}</Text>
                                <Text style={styles.chartBodyMetricValue}>{comparisonItemTwo.value}</Text>
                            </View>
                            <View style={styles.chartBodyMetric}>
                                <Text style={styles.chartBodyMetricLabel}>{varianceAmount.label}</Text>
                                <Text style={styles.chartBodyMetricValue}>{varianceAmount.value}</Text>
                            </View>
                            <View style={styles.chartBodyMetric}>
                                <Text style={styles.chartBodyMetricLabel}>{variancePercent.label}</Text>
                                <Text style={styles.chartBodyMetricValue}>{variancePercent.value}</Text>
                            </View>
                        </View>
                    }
                    <Image src={chartData} />
                </View>
            </View>
        )
    }

    const coverPage = (
        <Page size="LETTER" style={styles.coverPage}>
            <View style={styles.overlayContainer}>
                <Image
                    src={{
                        uri: clientBranding.banner?.startsWith("http") ? `${clientBranding.banner}?cachebuster=${Date.now()}` : `/${clientBranding.banner}`,
                        method: "GET",
                        headers: {},
                        body: ""
                    }}
                    style={backgroundImageStyle}
                />
                <View style={{ ...styles.colorOverlay, backgroundColor: clientBranding.colorPrimary }} />
                <View style={{ ...styles.coverPageBanner, backgroundColor: clientBranding.colorPrimary }}>
                    <Text style={styles.propertyName}>{data.property.name}</Text>
                    <Text style={styles.presentedBy}>{propertyDisplayBudgetYear(data.property)} Operating Budget</Text>
                    <Text style={styles.presentedBy}>Presented by {data.user.clientDisplayName}</Text>
                    <View style={{ ...styles.brandColorBand, backgroundColor: clientBranding.colorSecondary }}>
                        {/* TODO:bowman this is a switch on our special tri-color asset living branding, but should be revisited after deadline */}
                        {assetLivingWorkspaceIds.includes(data.user.clientId) ?
                                <>
                                    <View style={{ ...styles.colorBar, ...styles.colorBarBlue }}></View>
                                    <View style={{ ...styles.colorBar, ...styles.colorBarGreen }}></View>
                                    <View style={{ ...styles.colorBar, ...styles.colorBarOrange }}></View>
                                </>
                                : <></>
                        }
                    </View>
                </View>
            </View>
            <View style={styles.coverFooterBranding}>
                <Image src={brandLight} style={styles.footerVizLogo} />
                {
                    clientBranding.logo && <>
                        <Image src={addRounded} style={styles.footerBrandJoinIcon} />
                        <Image
                            src={{
                                uri: clientBranding.logo?.startsWith("http") ? `${clientBranding.logo}?cachebuster=${Date.now()}` : `${clientBranding.logo}`,
                                method: "GET",
                                headers: {},
                                body: ''
                            }}
                            style={coverLogoStyle}
                        />
                    </>
                }
            </View>
        </Page>
    );

    let analysisMetricBlocks = <></>;
    if(execSummaryMetricData['mktRent']) {
        analysisMetricBlocks = <>
            <View style={styles.metricBlockRow}>
                {buildMetricBlock(execSummaryMetricData['mktRent'], 'mktRent')}
                {buildMetricBlock(execSummaryMetricData['occupancy'], 'occupancy')}
            </View>

            <View style={styles.metricBlockRow}>
                {buildMetricBlock(execSummaryMetricData['resTurn'], 'resTurn')}
                {buildMetricBlock(execSummaryMetricData['tradeouts'], 'tradeouts')}
            </View>
        </>;
    } else {
        analysisMetricBlocks = <>
            <View style={styles.metricBlockRow}>
                {buildMetricBlock(execSummaryMetricData['occupancy'], 'occupancy')}
                {buildMetricBlock(execSummaryMetricData['resTurn'], 'resTurn')}
            </View>

            <View style={styles.metricBlockRow}>
                {buildMetricBlock(execSummaryMetricData['tradeouts'], 'tradeouts')}
            </View>
        </>;
    }

    const analysis = (
        <Page size="LETTER" style={styles.page}>
            {renderSectionAnchor("analysis")}
            <View style={styles.pageTitleWrapper} fixed>
                <Text style={styles.pageTitle}>Analysis</Text>
                <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
            </View>
            <View style={{ marginBottom: 24 }}>
                <Text style={{ ...styles.title, ...styles.titleSmall }}>Executive Summary</Text>
                {customNarrativeParagraphs.length > 0 ?
                    customNarrativeParagraphs.map((paragraph, index) =>
                        <Text key={index} style={{ ...styles.paragraph, marginTop: index > 0 ? 12 : 0 }}>
                            {paragraph}
                        </Text>
                    )
                    :
                    <Text style={styles.paragraph}>
                        {fullExecutiveSummaryStatementNOIWithoutExpense} {executiveSummaryStatementTotalExpense}
                    </Text>
                }
            </View>

            <View wrap={false}>
                <View style={{ marginBottom: 24 }}>
                    <Text style={{ ...styles.title, ...styles.titleSmall }}>Rent Summary</Text>
                    <Text style={styles.paragraph}>
                        {rentsStatementSummary}
                    </Text>
                </View>

                {analysisMetricBlocks}

                <View>
                    <Text style={{ ...styles.title, ...styles.titleSmall }}>Financial Summary</Text>
                    <Text style={styles.paragraph}>
                        {executiveSummaryStatementNOI}
                    </Text>
                </View>
            </View>

            {pageFooter()}
        </Page>
    );

    const opMetricCharts = (
        <Page size="LETTER" style={styles.page}>
            {renderSectionAnchor("ops-metric-charts")}
            <View style={styles.pageTitleWrapper} fixed>
                <Text style={styles.pageTitle}>Operational Metric Charts</Text>
                <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
            </View>

            <View style={styles.chartsPageWrapper}>
                {Array.from(charts.entries()).map(chart => buildChartView(chart[0], chart[1]))}
            </View>

            {pageFooter()}
        </Page>
    );

    const revenueTables = (
        <Page size="LETTER" style={styles.page} orientation="landscape">
            {renderSectionAnchor("rev-summary")}
            <View style={styles.pageTitleWrapper} fixed>
                <Text style={styles.pageTitle}>Revenue Summary</Text>
                <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
            </View>

            {revenueSummaryData.length > 0 ?
                revenueSummaryData.map((rowData, index) => renderRevenueTable(rowData, index))
                : <Text style={styles.paragraph}>No revenue summary table data</Text>
            }

            {pageFooter()}
        </Page>
    );

    function buildReportTableView(title: string, subtitle: string, tableData: ReportTableData) {
        let reforecastHeader = data.property.reforecastYear.toString();
        if(data.isUsingCustomT12) {
            reforecastHeader = `${customT12Text}`;
        }
        return (
            <View wrap={false} style={{ ...styles.cardWrapper, ...styles.cardWrapperFull }}>
                <View style={styles.cardHeader}>
                    <Text style={styles.cardHeaderTitle}>{title}</Text>
                    <Text style={styles.cardHeaderSubtitle}>{subtitle}</Text>
                </View>

                <View style={{ ...styles.cardBody, ...styles.cardBodyFlush }}>
                    <View style={styles.tableRow}>
                        <View style={{ ...styles.tableCellWrapper, ...styles.reportTableNameCol, ...styles.boldText }}>
                            <Text style={styles.tableCell}>Description</Text>
                        </View>

                        <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...styles.boldText }}>
                            <Text style={styles.tableCell}>{reforecastHeader} Total</Text>
                        </View>

                        <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...styles.boldText }}>
                            <Text style={styles.tableCell}>{propertyDisplayBudgetYear(data.property)} Total</Text>
                        </View>

                        <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...styles.boldText }}>
                            <Text style={styles.tableCell}>Growth $</Text>
                        </View>

                        <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...styles.boldText }}>
                            <Text style={styles.tableCell}>Growth %</Text>
                        </View>

                        <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...styles.boldText, ...styles.finalCol }}>
                            <Text style={styles.tableCell}>{propertyDisplayBudgetYear(data.property)}/Unit</Text>
                        </View>
                    </View>

                    {
                        tableData.rows.map((row, rowIndex) => {
                            let wrapperStyles = {};

                            if (rowIndex == tableData.rows.length - 1) {
                                wrapperStyles = { ...wrapperStyles, ...styles.finalRow };
                            }

                            return (
                                <View style={styles.tableRow} key={`row-${rowIndex}`}>
                                    <View style={{ ...styles.tableCellWrapper, ...styles.reportTableNameCol, ...wrapperStyles }}>
                                        <Text style={styles.tableCell}>{row.name}</Text>
                                    </View>

                                    <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...wrapperStyles }}>
                                        <Text style={styles.tableCell}>{row.reforecastTotal === null ? "-" : formatterDollarUSNoDecimal.format(row.reforecastTotal)}</Text>
                                    </View>

                                    <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...wrapperStyles }}>
                                        <Text style={styles.tableCell}>{row.budgetTotal === null ? "-" : formatterDollarUSNoDecimal.format(row.budgetTotal)}</Text>
                                    </View>

                                    <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...wrapperStyles }}>
                                        <Text style={{
                                            ...styles.tableCell,
                                            color: row.varianceAmount === null ? "" : getVarianceColor(row.varianceAmount, row.componentType, row.negateAtComponent)
                                        }}>
                                            {row.varianceAmount === null ? "-" : formatterDollarUSNoDecimal.format(row.varianceAmount)}
                                        </Text>
                                    </View>

                                    <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...wrapperStyles }}>
                                        <Text style={{
                                            ...styles.tableCell,
                                            color: row.varianceAmount === null ? "" : getVarianceColor(row.varianceAmount, row.componentType, row.negateAtComponent)
                                        }}>
                                            {row.variancePercent === null ? "-" : formatterPercentWithSign.format(row.variancePercent)}
                                        </Text>
                                    </View>

                                    <View style={{ ...styles.tableCellWrapper, ...styles.reportTableDataCol, ...styles.finalCol, ...wrapperStyles }}>
                                        <Text style={styles.tableCell}>{row.budgetPerUnit === null ? "-" : formatterDollarUSNoDecimal.format(row.budgetPerUnit)}</Text>
                                    </View>
                                </View>
                            )
                        })
                    }
                </View>
            </View>
        );
    }

    function renderPayrollTable(rows: string[][]) {
        const columnCount = 4;
        const rowCount = rows.length;

        return (
            <View wrap={false} style={{ ...styles.cardWrapper, ...styles.cardWrapperFull }}>
                <View style={{ ...styles.cardBody, ...styles.cardBodyFlush }}>
                    {
                        rows.map((row, rowIndex) =>
                            <View style={styles.tableRow} key={`row-${rowIndex}`}>
                                {
                                    row.map((r, colIndex) => {
                                        let wrapperStyles = {};
                                        let cellStyles = {};

                                        if (colIndex == 0) {
                                            wrapperStyles = styles.reportTableNameCol;
                                        } else if (colIndex > 0) {
                                            wrapperStyles = styles.payrollTableDataCol;
                                        }

                                        if (colIndex == columnCount - 1) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.finalCol };
                                        }

                                        if (rowIndex == 0) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.boldText };
                                        }

                                        if (rowIndex == rowCount - 1) {
                                            wrapperStyles = { ...wrapperStyles, ...styles.finalRow };
                                        }

                                        return (
                                            <View style={{ ...styles.tableCellWrapper, ...wrapperStyles }} key={`row-${rowIndex}-cell-${colIndex}`}>
                                                <Text style={{ ...styles.tableCell, ...cellStyles }}>{r}</Text>
                                            </View>
                                        )
                                    })
                                }
                            </View>
                        )
                    }
                </View>
            </View>
        );
    }

    function renderRenovationsTable(renoData: RenovationsSummaryTableData) {
        return (
            <View wrap={false} style={{ ...styles.cardWrapper, ...styles.cardWrapperFull }}>
                <View style={{ ...styles.cardBody, ...styles.cardBodyFlush }}>

                    <View style={{...styles.tableRow, ...styles.boldText}}>
                        <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableRowHeader }}>
                            <Text style={styles.tableCell}></Text>
                        </View>
                        <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableDataCol}}>
                            <Text style={styles.tableCell}>COST</Text>
                        </View>
                        <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableDataCol}}>
                            <Text style={styles.tableCell}>PREM</Text>
                        </View>
                        {renoData.months.map(month =>
                            <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableDataCol}}>
                                <Text style={styles.tableCell}>{month}</Text>
                            </View>
                        )}
                        <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableDataCol, ...styles.finalCol, ...styles.greyCell}}>
                            <Text style={styles.tableCell}>TOTAL</Text>
                        </View>
                    </View>

                    {renoData.rows.map((row, rowIndex) => {
                        let wrapperStyles = {};
                        const showCostAndPremium = row.rowType === "renoPackage";
                        const onLastRow = rowIndex === renoData.rows.length - 1;

                        if (onLastRow) {
                            wrapperStyles = {...wrapperStyles, ...styles.finalRow};
                        }
                        if (row.rowType !== "property" && row.rowType !== "unitType") {
                            wrapperStyles = {...wrapperStyles, ...styles.textAlignRight};
                        }
                        if (row.rowType === "totalExpiringMoveOuts" || row.rowType === "totalRenovations") {
                            wrapperStyles = {...wrapperStyles, ...styles.greyCell};
                        }
                        if (row.rowType === "totalExpiringMoveOuts" && !onLastRow) {
                            wrapperStyles = {...wrapperStyles, ...styles.thickBorderBottom};
                        }
                        if (row.rowType === "unitType") {
                            wrapperStyles = {...wrapperStyles, ...styles.hideBorderRight};
                        }

                        return (
                            <View style={{...styles.tableRow}} key={`row-${rowIndex}`}>
                                <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableRowHeader, ...wrapperStyles }}>
                                    <Text style={styles.tableCell}>{row.label}</Text>
                                </View>

                                <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableDataCol, ...wrapperStyles}}>
                                    <Text style={styles.tableCell}>{showCostAndPremium ? formatterDollarUSNoDecimal.format(row.cost ?? 0) : ""}</Text>
                                </View>

                                <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableDataCol, ...wrapperStyles}}>
                                    <Text style={styles.tableCell}>{showCostAndPremium ? formatterDollarUSNoDecimal.format(row.premium ?? 0) : ""}</Text>
                                </View>

                                {row.bdgtValues.map(bdgtVal =>
                                    <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableDataCol, ...wrapperStyles}}>
                                        <Text style={styles.tableCell}>{bdgtVal == null ? "" : round(bdgtVal, 1)}</Text>
                                    </View>
                                )}

                                <View style={{ ...styles.tableCellWrapper, ...styles.renovationsTableDataCol, ...wrapperStyles, ...styles.finalCol, ...styles.greyCell, ...styles.boldText}}>
                                    <Text style={styles.tableCell}>{row.rowType !== "unitType" ? round(row.bdgtValues.map(x => x ?? 0).sum(), 1) : ""}</Text>
                                </View>
                            </View>
                        );
                    })}
                </View>
            </View>
        );
    }

    let reportTables = data.reportTables;
    if(data.isUsingCustomT12) {
        reportTables = data.customT12ReportTables;
    }
    const financialTables = (
        <Page size="LETTER" style={styles.page}>
            {renderSectionAnchor("financial-tables")}
            <View style={styles.pageTitleWrapper} fixed>
                <Text style={styles.pageTitle}>Financial Tables</Text>
                <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
            </View>

            {reportTables.map((reportTable, i) => {
                let customVs = "";
                if(data.isUsingCustomT12) {
                    customVs = `${customT12Text} (${data.customT12Label}) vs `;
                }
                return <View key={`reportTable-${i}`}>
                    {buildReportTableView(reportTable.name, `${customVs}${propertyDisplayBudgetYear(data.property) % 100} BDGT`, reportTable)}
                </View>;
            })}

            {pageFooter()}
        </Page>
    );

    const financialOverview = (
        <Page size="LETTER" style={styles.page} orientation="landscape">
            {renderSectionAnchor("financial-overview")}
            <View style={styles.pageTitleWrapper} fixed>
                <Text style={styles.pageTitle}>Financial Overview</Text>
                <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
            </View>
            {financialOverviewData.length > 0 ?
                financialOverviewData.map((rowData) => renderFinancialsTable(rowData))
                : <Text style={styles.paragraph}>No financial table data</Text>
            }

            {pageFooter()}
        </Page>
    );

    const payrollTables = (
        <Page size="LETTER" style={styles.page} orientation="landscape">
            {renderSectionAnchor("payroll")}
            <View style={styles.pageTitleWrapper} fixed>
                <Text style={styles.pageTitle}>Payroll</Text>
                <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
            </View>

            {payrollTableData.length > 0 ?
                payrollTableData.map((rowData) => renderPayrollTable(rowData))
                : <Text style={styles.paragraph}>No payroll table data</Text>
            }

            {pageFooter()}
        </Page>
    );

    const signaturePage = (
        <Page size="LETTER" style={styles.page} orientation="portrait">
            {renderSectionAnchor("budgetsignoff")}
            <View style={styles.pageTitleWrapper} fixed>
                <Text style={styles.pageTitle}>Budget Sign-Off</Text>
                <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
            </View>

            {renderSignOffPageTable()}

            <View style={styles.fieldGroup}>
                <Text style={styles.signatureField}>Company Representative:</Text>
                <Text style={styles.signatureField}>Date:</Text>
            </View>

            <View style={styles.fieldGroup}>
                <Text style={styles.signatureField}>Owner:</Text>
                <Text style={styles.signatureField}>Date:</Text>
            </View>

            {pageFooter()}
        </Page>
    );

    function renovationsPages() {
        const pages = [];
        let onFirstPageOfRenovations = true;

        if (data.renovationsData.rows.length === 0) {
            return (
                <Page size="LETTER" style={styles.page} orientation="landscape">
                    {renderSectionAnchor("renovations")}
                    <View style={styles.pageTitleWrapper} fixed>
                        <Text style={styles.pageTitle}>Planned Renovations</Text>
                        <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
                    </View>
                    <Text style={styles.paragraph}>No planned renovations</Text>
                    {pageFooter()}
                </Page>
            );
        }

        // using ROWS_PER_PAGE_RENOVATIONS to slice the row data gives us the number of pages/tables we need
        // ROWS_PER_PAGE_RENOVATIONS is how many *actual* rows fit on a page - does NOT include the header row
        for (let i = 0; i < data.renovationsData.rows.length; i += ROWS_PER_PAGE_RENOVATIONS) {
            const rowsForThisPage: RenovationsSummaryTableData = {
                rows: data.renovationsData.rows.slice(i, i + ROWS_PER_PAGE_RENOVATIONS),
                months: data.renovationsData.months,
            };
            pages.push(
                <Page size="LETTER" style={styles.page} orientation="landscape">
                    {onFirstPageOfRenovations && renderSectionAnchor("renovations")}
                    <View style={styles.pageTitleWrapper} fixed>
                        <Text style={styles.pageTitle}>Planned Renovations</Text>
                        <Text style={styles.pageTitlePropertyName}>{data.property.name}</Text>
                    </View>
                    {renderRenovationsTable(rowsForThisPage)}
                    {pageFooter()}
                </Page>
            );
            onFirstPageOfRenovations = false;
        }

        return pages;
    }

    return (
        <Document>
            {coverPage}
            {tableOfContents}
            {analysis}
            {data.reportTables.length > 0 && financialTables}
            {opMetricCharts}
            {financialOverview}
            {revenueTables}
            {renovationsPages()}
            {payrollTables}
            {clientBranding.showSignOffPage && signaturePage}
        </Document>
    );
}
